// F7: the signed-in user's notification feed — the initial GET /me/notifications, live pushes
// over the shared WS ({type:"notification"}), mark-read, and the OS-notification side effect
// (desktop plugin / Web Notifications) for pushes that arrive while the app is unfocused.
import { useCallback, useEffect, useRef, useState } from "react";
import { progressSocket, type WsNotification } from "../api/ws";
import {
  getNotificationPrefs,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  putNotificationPrefs,
  type NotificationOut,
} from "./api";
import { internalPath, notificationText } from "./display";
import {
  getOsPermission,
  osNotificationsSupported,
  requestOsPermission,
  shouldShowOsNotification,
  showOsNotification,
  type OsPermission,
} from "./osNotify";
import { isDesktop } from "../desktop";

/** How long after an unfocused OS notification the next window focus still counts as "the
 * user clicked it" (the desktop plugin reports no click). */
export const CLICK_FOCUS_WINDOW_MS = 30_000;

interface PendingOpen {
  path: string;
  at: number;
}

/** `all`: fetch every page on load rather than just the first (the notifications page shows the
 * whole feed; the bell only needs the newest). */
export function useNotifications(onOpenPath: (path: string) => void, { all = false }: { all?: boolean } = {}) {
  const [items, setItems] = useState<NotificationOut[]>([]);
  const [unread, setUnread] = useState(0);
  /** Cursor for the next page of the feed; null at the end. With `all`, set only when a page
   * failed to load (the notifications page's "Load more" retry). */
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [permission, setPermission] = useState<OsPermission>("unsupported");
  /** True once a relevant event arrived and the user hasn't decided on OS notifications yet. */
  const [askPermission, setAskPermission] = useState(false);
  const desktopPrefs = useRef<Map<string, boolean>>(new Map());
  const permissionRef = useRef<OsPermission>("unsupported");
  const pending = useRef<PendingOpen | null>(null);
  const openRef = useRef(onOpenPath);
  openRef.current = onOpenPath;

  const reload = useCallback(() => {
    void (async () => {
      let res;
      try {
        res = await listNotifications();
      } catch {
        // Best-effort: the bell keeps what it had.
        return;
      }
      let list = res.items;
      let cursor = res.next_cursor ?? null;
      while (all && cursor) {
        try {
          const more = await listNotifications(cursor);
          const seen = new Set(list.map((n) => n.id));
          list = [...list, ...more.items.filter((n) => !seen.has(n.id))];
          cursor = more.next_cursor ?? null;
        } catch {
          // Show what arrived; the cursor left behind keeps "Load more" as a retry.
          break;
        }
      }
      setItems(list);
      setUnread(res.unread_count);
      setNextCursor(cursor);
    })();
  }, [all]);

  const cursorRef = useRef<string | null>(null);
  cursorRef.current = nextCursor;
  const loadingMoreRef = useRef(false);

  /** Appends the next page (items already shown are skipped — a push can arrive in between). */
  const loadMore = useCallback(async () => {
    const cursor = cursorRef.current;
    if (!cursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await listNotifications(cursor);
      setItems((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...res.items.filter((n) => !seen.has(n.id))];
      });
      setNextCursor(res.next_cursor ?? null);
    } catch {
      // Best-effort: the list keeps what it had and "Load more" stays available.
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const onVisible = () => {
      if (document.visibilityState === "visible") reload();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reload]);

  useEffect(() => {
    if (!osNotificationsSupported()) return;
    void getOsPermission().then((p) => {
      permissionRef.current = p;
      setPermission(p);
    });
    getNotificationPrefs()
      .then((res) => {
        desktopPrefs.current = new Map(
          res.items.filter((p) => p.channel === "desktop").map((p) => [p.kind, p.enabled]),
        );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    return progressSocket.subscribeNotifications((n: WsNotification) => {
      setItems((prev) => (prev.some((x) => x.id === n.id) ? prev : [{ ...n, read_at: null }, ...prev]));
      setUnread((c) => c + 1);

      if (!osNotificationsSupported()) return;
      const perm = permissionRef.current;
      if (perm === "prompt") {
        // First relevant event with no decision yet: surface the DS Callout in the bell.
        setAskPermission(true);
        return;
      }
      if (
        shouldShowOsNotification({
          prefEnabled: desktopPrefs.current.get(n.kind) === true,
          permission: perm,
          focused: document.hasFocus(),
        })
      ) {
        const text = notificationText(n);
        const path = internalPath(n.payload?.link);
        if (path) pending.current = { path, at: Date.now() };
        void showOsNotification(text.title, text.body, path ? () => openRef.current(path) : undefined).catch(() => {});
      }
    });
  }, []);

  // Desktop: the plugin has no click callback, so focusing the window shortly after an OS
  // notification was shown opens its link (and it is only ever used once).
  useEffect(() => {
    if (!isDesktop()) return;
    const onFocus = () => {
      const p = pending.current;
      pending.current = null;
      if (p && Date.now() - p.at <= CLICK_FOCUS_WINDOW_MS) openRef.current(p.path);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const itemsRef = useRef<NotificationOut[]>([]);
  itemsRef.current = items;

  const markRead = useCallback(
    (id: string) => {
      // Decide from the current list, not inside a state updater (which runs later).
      const wasUnread = itemsRef.current.some((n) => n.id === id && !n.read_at);
      if (wasUnread) {
        const now = new Date().toISOString();
        setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: now } : n)));
        setUnread((c) => Math.max(0, c - 1));
      }
      markNotificationRead(id).catch(reload);
    },
    [reload],
  );

  const markAllRead = useCallback(() => {
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    setUnread(0);
    markAllNotificationsRead().catch(reload);
  }, [reload]);

  /** The Callout's "Turn on": ask the OS, and on grant turn the desktop channel on for every kind. */
  const enableOsNotifications = useCallback(async () => {
    const p = await requestOsPermission();
    permissionRef.current = p;
    setPermission(p);
    setAskPermission(false);
    if (p === "granted") {
      const kinds = Array.from(
        new Set([...desktopPrefs.current.keys(), ...(await getNotificationPrefs()).items.map((i) => i.kind)]),
      );
      const res = await putNotificationPrefs(kinds.map((kind) => ({ kind, channel: "desktop", enabled: true })));
      desktopPrefs.current = new Map(
        res.items.filter((i) => i.channel === "desktop").map((i) => [i.kind, i.enabled]),
      );
    }
  }, []);

  const dismissAsk = useCallback(() => setAskPermission(false), []);

  return {
    items,
    unread,
    nextCursor,
    loadingMore,
    loadMore,
    permission,
    askPermission,
    markRead,
    markAllRead,
    enableOsNotifications,
    dismissAsk,
    reload,
  };
}
